import type { AccountSearchResult } from "@wallyo/contracts/console/accounts";
import { useState, type FormEvent } from "react";
import { t } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { describeKind, formatMonth, nameOf, regionName } from "./customer-format";
import { customerHref } from "./customer-route";
import { FeatureLock } from "./FeatureLock";
import { queryProblem, type SearchState } from "./search-state";

export type CustomerSearchViewProps = {
  /** The query in the URL: the search on screen. */
  query: string;
  state: SearchState;
  canSearch: boolean;
  /** Results link to their page only for someone who can open it. */
  canView: boolean;
  roles: readonly string[];
  onSearch: (query: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
};

/** Clients & pros: one field for a name, phone, email or account id, then the matching accounts, newest first. */
export function CustomerSearchView(props: CustomerSearchViewProps) {
  const { state } = props;
  return (
    <>
      <PageTop title={t("screen.customers")} sub={t("sub.customers")} />
      {props.canSearch ? (
        <div className="card">
          {/* Remounted when the link changes, so the field follows a shared or cleared link. */}
          <SearchForm key={props.query} query={props.query} onSearch={props.onSearch} />
          <SearchResults {...props} />
          {state.status === "ready" && state.results.length > 0 ? (
            <div className="audit-more">
              {state.moreError ? (
                <>
                  <ErrorNotice error={state.moreError} />
                  <p className="foot">{t("customers.search.moreFailed")}</p>
                </>
              ) : null}
              <div className="btns">
                {state.nextCursor ? (
                  <button className="btn" type="button" onClick={props.onLoadMore} disabled={state.loadingMore}>
                    {state.loadingMore
                      ? t("customers.search.loadingMore")
                      : state.moreError
                        ? t("customers.search.retry")
                        : t("customers.search.loadMore")}
                  </button>
                ) : null}
                <span className="meta" role="status">
                  {state.results.length === 1
                    ? t("customers.search.countOne")
                    : t("customers.search.count", { count: state.results.length })}
                  {state.nextCursor ? "" : ` · ${t("customers.search.end")}`}
                </span>
              </div>
            </div>
          ) : null}
          <p className="foot">{t("customers.search.masked")}</p>
        </div>
      ) : (
        <FeatureLock
          lead={t("customers.search.locked")}
          feature="SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT"
          roles={props.roles}
        />
      )}
    </>
  );
}

function SearchForm({ query, onSearch }: { query: string; onSearch: (query: string) => void }) {
  const [draft, setDraft] = useState(query);
  const [problem, setProblem] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = queryProblem(draft);
    setProblem(found);
    if (!found) onSearch(draft.trim());
  };
  return (
    <form className="customer-search" role="search" onSubmit={submit} noValidate>
      <div className="field">
        <label htmlFor="customer-query">{t("customers.search.label")}</label>
        <div className="search-row">
          <input
            id="customer-query"
            type="search"
            value={draft}
            placeholder={t("customers.search.placeholder")}
            autoComplete="off"
            spellCheck={false}
            maxLength={100}
            aria-describedby="customer-query-hint"
            aria-invalid={problem ? true : undefined}
            onChange={(event) => {
              setDraft(event.target.value);
              setProblem(null);
            }}
          />
          <button className="btn primary" type="submit">
            {t("customers.search.submit")}
          </button>
        </div>
        <span className="meta" id="customer-query-hint">
          {t("customers.search.hint")}
        </span>
        {problem ? <span className="field-problem">{problem}</span> : null}
      </div>
    </form>
  );
}

function SearchResults({ query, state, canView, onRetry }: CustomerSearchViewProps) {
  switch (state.status) {
    case "idle":
      return <div className="empty">{t("customers.search.idle")}</div>;
    case "loading":
      return (
        <div className="empty" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span>{t("customers.search.loading")}</span>
        </div>
      );
    case "error":
      return (
        <div className="audit-error">
          <p className="foot">{t("customers.search.loadFailed")}</p>
          <ErrorNotice error={state.error} />
          <div>
            <button className="btn primary" type="button" onClick={onRetry}>
              {t("customers.search.retry")}
            </button>
          </div>
        </div>
      );
    case "ready":
      break;
  }
  const matchedBy =
    state.matchedBy === "phone" && state.normalisedPhone
      ? t("customers.search.matchedByPhone", { phone: state.normalisedPhone })
      : state.matchedBy
        ? t(`customers.search.matchedBy.${state.matchedBy}` as const)
        : "";
  if (state.results.length === 0) {
    return (
      <div className="empty" role="status">
        <span>{t("customers.search.empty", { query })}</span>
        {matchedBy ? <span className="meta">{matchedBy}</span> : null}
      </div>
    );
  }
  return (
    <>
      {matchedBy ? <p className="meta search-matched">{matchedBy}</p> : null}
      <div className="table-wrap">
        <table className="customer-results">
          <thead>
            <tr>
              <th>{t("customers.col.name")}</th>
              <th>{t("customers.col.contact")}</th>
              <th>{t("customers.col.kind")}</th>
              <th>{t("customers.col.region")}</th>
              <th>{t("customers.col.since")}</th>
            </tr>
          </thead>
          <tbody>
            {state.results.map((result) => (
              <ResultRow key={result.userId} result={result} canView={canView} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ResultRow({ result, canView }: { result: AccountSearchResult; canView: boolean }) {
  const name = nameOf(result);
  const open = () => {
    window.location.hash = customerHref(result.userId);
  };
  return (
    <tr
      className={[canView ? "clickable" : "", result.deleted ? "deleted" : ""].filter(Boolean).join(" ") || undefined}
      data-user-id={result.userId}
      onClick={canView ? open : undefined}
    >
      <td>
        {canView ? (
          <a href={customerHref(result.userId)} className="row-link" onClick={(event) => event.stopPropagation()}>
            {name}
          </a>
        ) : (
          <b>{name}</b>
        )}
        {result.deleted ? (
          <>
            {" "}
            <span className="pill bad">{t("customers.deleted")}</span>
          </>
        ) : null}
      </td>
      <td>
        <span className="mono">{result.maskedPhone ?? "—"}</span>
        <br />
        <span className="meta mono">{result.maskedEmail ?? "—"}</span>
      </td>
      <td>{describeKind(result)}</td>
      <td>{regionName(result.region)}</td>
      <td>{formatMonth(result.createdAt)}</td>
    </tr>
  );
}
